import {
  Button,
  Divider,
  EButtonColorVariant,
  ELinkVariant,
  EModalVariants,
  Link,
  Modal,
} from '@outdoorsyco/bonfire';
import React from 'react';
import { useIntl } from 'react-intl';

import { useBreakpoint } from '@/hooks/useBreakpoint';
import { ESearchWidgetFilter } from '@/services/analytics/search/types';

import { DesktopFilterContent } from './DesktopFilterContent';

type TTFilterContentNextProps = {
  onNext: () => void;
  nextButtonLabel?: string;
  onSearch?: undefined;
};

type TTFilterContentSearchProps = {
  onNext?: undefined;
  nextButtonLabel?: undefined;
  onSearch?: () => void;
};

export type TFilterContentSharedProps = (TTFilterContentNextProps | TTFilterContentSearchProps) & {
  onBack?: () => void;
  isOpened: boolean;
  onClose: () => void;
  fullHeight?: boolean;
  onTrackFilterToggled?: (
    oldSelection: Record<string, unknown>,
    newSelection: Record<string, unknown>,
    modifiedWidgetFilter: ESearchWidgetFilter,
  ) => void;
  buttonRef?: React.RefObject<HTMLDivElement>;
  searchBarRef?: React.RefObject<HTMLDivElement>;
  align: 'left' | 'center' | 'right';
};

type TFilterContentProps = TFilterContentSharedProps & {
  header: React.ReactNode;
  content: React.ReactNode;
  footerContent?: React.ReactNode;
};

export const FilterContent = ({
  onBack,
  onNext,
  nextButtonLabel,
  onSearch,
  header,
  content,
  footerContent,
  isOpened,
  onClose,
  fullHeight,
  ...props
}: TFilterContentProps) => {
  const intl = useIntl();
  const { isAboveDesktop } = useBreakpoint();

  if (isAboveDesktop) {
    return (
      <DesktopFilterContent
        isOpened={isOpened}
        content={content}
        footerContent={footerContent}
        buttonRef={props.buttonRef}
        searchBarRef={props.searchBarRef}
        align={props.align}
      />
    );
  }

  const hasSingleAction = [onBack, onNext, onSearch].filter(x => x).length === 1;

  return (
    <Modal
      isOpen={isOpened}
      onClose={onClose}
      variant={EModalVariants.Bottom}
      dialogClassName={fullHeight ? 'h-full' : ''}>
      <Modal.Actions />

      <Modal.Header>
        <div>{header}</div>
      </Modal.Header>

      <Divider />

      <Modal.Content className="flex-grow pb-6 mt-5">{content}</Modal.Content>

      {(onBack || onNext || onSearch || footerContent) && (
        <Modal.Footer className="p-0">
          <div className="w-full">
            <Divider />
            {footerContent && (
              <>
                {footerContent}
                <Divider />
              </>
            )}
            <div
              className={`flex items-center ${
                hasSingleAction ? 'justify-end' : 'justify-between'
              } p-4`}>
              {onBack && (
                <Link
                  variant={ELinkVariant.Underline}
                  label={intl.formatMessage({
                    defaultMessage: 'Back',
                    id: '96dVw1',
                    description: 'Home Search Widget > Back Button Label',
                  })}
                  onClick={onBack}
                />
              )}

              {onNext && (
                <Button
                  variant={EButtonColorVariant.Tertiary}
                  label={
                    nextButtonLabel ||
                    intl.formatMessage({
                      defaultMessage: 'Next',
                      id: 'PvuWTI',
                      description: 'Home Search Widget > Next Button Label',
                    })
                  }
                  onClick={onNext}
                />
              )}

              {onSearch && (
                <Button
                  variant={EButtonColorVariant.Tertiary}
                  label={intl.formatMessage({
                    defaultMessage: 'Search',
                    id: 'D5G/Wt',
                    description: 'Home Search Widget > Search Button Label',
                  })}
                  onClick={onSearch}
                />
              )}
            </div>
          </div>
        </Modal.Footer>
      )}
    </Modal>
  );
};
